import { Box, Icon, IconProps, Typography } from '@branch-messenger/willow-ui'
import {
  CheckCircleFilled,
  RefreshFilledCw,
} from '@branch-messenger/willow-ui/icons'
import { useSuspenseQuery } from '@tanstack/react-query'

import { AdvanceAgreement } from '@/containers/onDemandPay/components/AdvanceAgreement'
import { useTakeAdvance } from '@/containers/onDemandPay/TakeAdvance/TakeAdvanceContext'
import { advanceDelaysArticleUrl } from '@/lib/urls'
import { onDemandPayCardQueryOptions } from '@/queries/card'
import { useAdvanceQuote } from '@/selectors/ewa'
import { useOrg } from '@/selectors/organization'

import {
  AdvanceStepTitle,
  TakeAdvanceContentContainer,
  TakeAdvanceFooterButton,
} from '../../styles'
import { TakeAdvanceContent } from '../../TakeAdvanceContent'
import { TakeAdvanceFooter } from '../../TakeAdvanceFooter'

type AdvanceResultStatus = 'pending' | 'success'

export const iconMap: Record<NonNullable<AdvanceResultStatus>, IconProps> = {
  pending: { Icon: RefreshFilledCw, color: 'info' },
  success: { Icon: CheckCircleFilled, color: 'brand' },
}

export const AdvanceResultContent = () => {
  const { resetStep, reset, setModalOpen, takeAdvanceValues } = useTakeAdvance()

  const advanceAmount = takeAdvanceValues.advance_amount
  const transferId = takeAdvanceValues.transfer_id ?? ''
  const currentStatus = takeAdvanceValues.status
  const status = currentStatus === 'po pending' ? 'pending' : 'success'

  const { data: advanceQuote } = useAdvanceQuote(advanceAmount)
  const { data: onDemandPayCard } = useSuspenseQuery(
    onDemandPayCardQueryOptions()
  )
  const {
    data: { appName },
  } = useOrg()

  const title =
    status === 'pending'
      ? 'Your advance is being processed.'
      : 'Money is on the move.'
  const timeOfArrival = status === 'pending' ? '30 minutes' : 'within minutes'

  const destination = `${onDemandPayCard?.network} debit card ending in ${onDemandPayCard?.last4} for ${appName}`
  const iconProps = iconMap[status]

  const close = () => {
    resetStep()
    reset()
    setModalOpen(false)
  }

  return (
    <TakeAdvanceContentContainer>
      <TakeAdvanceContent>
        <Box
          $display="flex"
          $direction="column"
          $align="initial"
          $justify="space-between"
          $gap={4}
          $flex={1}
        >
          <Box $display="flex" $direction="column" $align="initial" $gap={4}>
            <Box $display="flex" $justify="center" $fullWidth>
              <Icon {...iconProps} height={128} width={128} />
            </Box>
            <AdvanceStepTitle>{title}</AdvanceStepTitle>
            <Typography>{`${advanceQuote?.receivedAmount} will be transferred to your ${destination} ASAP.`}</Typography>
            <Typography>
              {`Most advances settle ${timeOfArrival}, but delays can happen. Learn more about `}
              <a
                href={advanceDelaysArticleUrl}
                target="_blank"
                rel="noreferrer"
              >
                potential delays here
              </a>
              .
            </Typography>
          </Box>
          <AdvanceAgreement
            buttonText="Save"
            description="Keep a copy of your records"
            transferId={transferId}
          />
        </Box>
      </TakeAdvanceContent>
      <TakeAdvanceFooter>
        <TakeAdvanceFooterButton onClick={close}>Done</TakeAdvanceFooterButton>
      </TakeAdvanceFooter>
    </TakeAdvanceContentContainer>
  )
}
